'use client';

import React, { useLayoutEffect, useRef } from 'react';
import { loadGsap } from '@/lib/gsapLazy';
import { cn } from '@/lib/twUtils';
import { MOTION_OK, prefersReducedMotion } from '@/lib/motion';

export interface StatProps {
  /** Valore mostrato a riposo e a fine animazione (es. "−70%") */
  value: string;
  label: string;
  /**
   * Se presente, il numero sale da 0 a `to` allo scroll; `prefix` e `suffix`
   * incorniciano la cifra, `grouped` la scrive con i punti delle migliaia
   * (1.300.000). Se `value` è diverso dalla cifra finale (es. «Oltre 1,3 mln»)
   * le cifre scorrono via come su un contagiri e la dicitura sale al loro posto.
   * Senza questa prop il valore resta statico. Solo dati semplici: Stat è un
   * client component e le pagine che lo montano sono server component.
   */
  count?: { to: number; prefix?: string; suffix?: string; grouped?: boolean };
  className?: string;
}

const valueClass = cn(
  'font-display font-bold text-primary',
  // I valori testuali («Ogni giorno») devono poter andare a capo senza
  // invadere la colonna accanto: leading stretto ma non `none`.
  'text-[clamp(1.875rem,3.4vw,2.625rem)] leading-[1.05] tracking-[-0.04em] text-balance'
);

/**
 * Numero grande + didascalia, con conteggio animato allo scroll. GSAP arriva a
 * richiesta (`lib/gsapLazy`): il barrel di `ds` porta Stat in ogni pagina, e
 * un import in cima lo avrebbe messo nel bundle di tutte.
 */
const Stat = ({ value, label, count, className }: StatProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    const digits = root?.querySelector<HTMLElement>('[data-stat-digits]');
    const final = root?.querySelector<HTMLElement>('[data-stat-final]');
    if (!root || !digits || !final || !count || prefersReducedMotion()) return;

    let cancelled = false;
    let mm: gsap.MatchMedia | undefined;
    loadGsap().then(({ gsap }) => {
      if (cancelled) return;
      mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        const { to, prefix = '', suffix = '', grouped = false } = count;
        const render = (n: number) => {
          const rounded = Math.round(n);
          return `${prefix}${grouped ? rounded.toLocaleString('it-IT') : rounded}${suffix}`;
        };

        // In attesa dello scroll si vede già lo zero, non un lampo del valore finale
        const counter = { n: 0 };
        digits.textContent = render(0);
        gsap.set(digits, { opacity: 1, yPercent: 0 });
        gsap.set(final, { opacity: 0, yPercent: 140 });

        const tl = gsap.timeline({
          scrollTrigger: { trigger: root, start: 'top 88%', once: true },
        });
        tl.to(counter, {
          n: to,
          duration: 1.4,
          ease: 'power2.out',
          onUpdate: () => {
            digits.textContent = render(counter.n);
          },
        });
        if (render(to) !== value) {
          // Rullo da contagiri: mentre rallentano sul traguardo le cifre
          // scorrono su, oltre il bordo della cella, e la dicitura sale da
          // sotto a prendere il loro posto. Impilate in verticale non si
          // sovrappongono mai; la dissolvenza è solo sulle code, per non
          // avere bordi duri al ritaglio.
          const roll = { duration: 0.55, ease: 'power2.inOut' };
          tl.addLabel('roll', '-=0.35')
            .to(digits, { yPercent: -140, ...roll }, 'roll')
            .to(digits, { opacity: 0, duration: 0.25, ease: 'power1.in' }, 'roll+=0.25')
            .to(final, { yPercent: 0, ...roll }, 'roll')
            .to(final, { opacity: 1, duration: 0.25, ease: 'power1.out' }, 'roll');
        }
      });
    });

    return () => {
      cancelled = true;
      mm?.revert();
    };
  }, [count, value]);

  return (
    <div ref={ref} className={className}>
      {count ? (
        // Cifre e dicitura condividono la stessa cella: la larghezza è quella
        // della scritta più lunga e non cambia mentre i numeri corrono, così
        // la didascalia sotto non si sposta. Il ritaglio serve al rullo; il
        // padding (compensato dal margine) lascia spazio ad ascendenti e
        // discendenti, che sporgono dalla riga con un leading così stretto.
        <div className={cn(valueClass, 'grid overflow-hidden py-[0.15em] -my-[0.15em]')}>
          <span data-stat-digits aria-hidden className="col-start-1 row-start-1 opacity-0" />
          <span data-stat-final className="col-start-1 row-start-1">
            {value}
          </span>
        </div>
      ) : (
        <div className={valueClass}>{value}</div>
      )}
      <div className="mt-2 max-w-[24ch] text-[0.90625rem] text-ink-muted">{label}</div>
    </div>
  );
};

export default Stat;
